/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import React, { useCallback } from 'react';

import { Toast } from 'src/common/components';

import { addNumberToAccount, addNumberToCampaign, fetchPhones } from '../api';
import { PhoneAction } from '../constants';
import {
  AccountProps,
  CampaignProps,
  PhoneActionType,
  PhoneActions,
  PhoneProps,
  PhoneStateType,
} from '../types';

import { usePhonesReducer } from './reducer';

interface UseActionsProps extends PhoneActions {
  state: PhoneStateType;
  dispatch: React.Dispatch<PhoneActionType>;
}

export const useActions = (): UseActionsProps => {
  const [state, dispatch] = usePhonesReducer();
  const { search, currentPage } = state;

  const setPhones = useCallback(
    (phones: PhoneProps[], count: number, error?: string): void =>
      dispatch({
        type: PhoneAction.SET_PHONES,
        phones: phones || [],
        count,
        error,
      }),
    [dispatch]
  );

  const setCampaigns = useCallback(
    (campaigns: CampaignProps[]): void =>
      dispatch({
        type: PhoneAction.SET_CAMPAIGNS,
        campaigns: campaigns || [],
      }),
    [dispatch]
  );

  const setAccounts = useCallback(
    (accounts: AccountProps[]): void =>
      dispatch({
        type: PhoneAction.SET_ACCOUNTS,
        accounts: accounts || [],
      }),
    [dispatch]
  );

  const setLoadingPhones = useCallback(
    (loading: boolean): void =>
      dispatch({
        type: PhoneAction.SET_LOADING_PHONES,
        loading,
      }),
    [dispatch]
  );

  const searchByNumber = useCallback(
    (phone: string): void =>
      dispatch({
        type: PhoneAction.SEARCH_BY_NUMBER,
        phone,
      }),
    [dispatch]
  );

  const searchByAccount = useCallback(
    (account: string): void =>
      dispatch({
        type: PhoneAction.SEARCH_BY_ACCOUNT,
        account,
      }),
    [dispatch]
  );

  const searchByCampaign = useCallback(
    (campaign: string): void =>
      dispatch({
        type: PhoneAction.SEARCH_BY_CAMPAIGN,
        campaign,
      }),
    [dispatch]
  );

  const setSelectedPhones = useCallback(
    (selectedPhones: string[]): void =>
      dispatch({
        type: PhoneAction.SET_SELECTED_PHONES,
        selectedPhones,
      }),
    [dispatch]
  );

  const paginate = useCallback(
    (page: number): void =>
      dispatch({
        type: PhoneAction.PAGINATE,
        page,
      }),
    [dispatch]
  );

  const assignToCampaign = useCallback(
    async (phones: string[], campaign: string): Promise<void> => {
      try {
        dispatch({
          type: PhoneAction.SET_ASSIGN_TO_CAMPAIGN,
          saving: true,
        });
        void (await addNumberToCampaign(phones, campaign));
        const list = await fetchPhones(search, currentPage);
        dispatch({
          type: PhoneAction.SET_PHONES,
          phones: list.data.results,
          count: list.data.count,
        });
        dispatch({
          type: PhoneAction.SET_SELECTED_PHONES,
          selectedPhones: [],
        });
        Toast.success('The phone(s) number has been assigned to the campaign.');
      } catch (error: any) {
        Toast.error(error?.message);
      } finally {
        dispatch({
          type: PhoneAction.SET_ASSIGN_TO_CAMPAIGN,
          saving: false,
        });
      }
    },
    [dispatch, search, currentPage]
  );

  const assignToAccount = useCallback(
    async (phones: string[], account: string): Promise<void> => {
      try {
        dispatch({
          type: PhoneAction.SET_ASSIGN_TO_ACCOUNT,
          saving: true,
        });
        void (await addNumberToAccount(phones, account));
        const list = await fetchPhones(search, currentPage);
        dispatch({
          type: PhoneAction.SET_PHONES,
          phones: list.data.results,
          count: list.data.count,
        });
        dispatch({
          type: PhoneAction.SET_SELECTED_PHONES,
          selectedPhones: [],
        });
        Toast.success('The phone(s) number has been assigned to the account.');
      } catch (error: any) {
        Toast.error(error?.message);
      } finally {
        dispatch({
          type: PhoneAction.SET_ASSIGN_TO_ACCOUNT,
          saving: false,
        });
      }
    },
    [dispatch, search, currentPage]
  );

  return {
    state,
    dispatch,
    setPhones,
    setLoadingPhones,
    searchByNumber,
    searchByAccount,
    searchByCampaign,
    setCampaigns,
    setAccounts,
    setSelectedPhones,
    paginate,
    assignToCampaign,
    assignToAccount,
  };
};
